import type { ReactNode } from "react";

import { useTipsRail } from "@/app/components/Tips";
import { cn } from "@/app/lib/utils";

/** The tips rail's column plus the grid gap, from `BuilderSection`. */
const RAIL_COLUMN = "xl:mr-[22rem]";

interface SectionActionsProps {
	/** The section's own buttons, unchanged. */
	children: ReactNode;
	/** The row's layout classes — `flex items-center justify-between` and friends. */
	className?: string;
}

/**
 * The bar carrying a section's Previous/Next (or Skip/Continue) buttons, pinned
 * to the bottom of the viewport so the way forward is on screen whatever the
 * section's length.
 *
 * Fixed, not sticky: sticky pins only while the content is taller than the
 * screen and drops to its natural place at the end of a short section, which put
 * the buttons halfway up the page on the shorter steps.
 *
 * Because it leaves the flow it cannot inherit the content column's geometry, so
 * `BuilderSection`'s is mirrored here — the sidebar's two widths on the left, its
 * padding and max width in the middle, and the tips rail's column held clear on
 * the right so the bar stops where the section does rather than running under the
 * tips. `bottom-9` matches the height of the mobile progress-dot strip so the two
 * stack instead of overlapping; from `md` those dots are hidden and the bar sits
 * on the floor.
 */
const SectionActions = ({ children, className }: SectionActionsProps) => {
	const { entries } = useTipsRail();
	const hasRail = entries.length > 0;

	return (
		<div className="fixed right-0 bottom-9 left-0 z-40 bg-white md:bottom-0 md:left-18 lg:left-[260px]">
			<div className="px-4 md:px-6">
				<div className={cn(hasRail && "mx-auto max-w-[1600px]")}>
					<div className={cn("border-gray-200 border-t bg-white py-3", hasRail && RAIL_COLUMN, className)}>
						{children}
					</div>
				</div>
			</div>
		</div>
	);
};

export default SectionActions;
